// -------------------------------
// EXPERIENCE INTERFACES
// -------------------------------
export interface PendingExperience {
    label?: string;
    timePeriod?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteExperience {
    awaitingLabel?: boolean;
}

export const startCreateExperienceFlow = (
    setPending: (data: PendingExperience | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    labelFromIntent?: string
) => {
    if (labelFromIntent) {
        setPending({ label: labelFromIntent });
        pushAIChat(`Got it. What is the time period for "${labelFromIntent}"?`);
        speak(`What is the time period for ${labelFromIntent}?`);
        return;
    }

    setPending({});
    pushAIChat("Sure! What should the experience level be called?");
    speak("What should the experience level be called?");
};

export const processPendingExperience = (
    text: string,
    pending: PendingExperience,
    setPending: (data: PendingExperience | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — LABEL
    if (!pending.label) {
        const cleanLabel = text.trim();
        if (!cleanLabel) {
            pushAIChat("Please provide a valid experience level.");
            speak("Please provide a valid experience level.");
            return;
        }

        setPending({ ...pending, label: cleanLabel });
        pushAIChat(`What is the time period for "${cleanLabel}"?`);
        speak(`What is the time period for ${cleanLabel}?`);
        return;
    }

    // STEP 2 — TIME PERIOD
    if (!pending.timePeriod) {
        setPending({ ...pending, timePeriod: text });
        pushAIChat("Should this experience be active or inactive?");
        speak("Should this experience be active or inactive?");
        return;
    }

    // STEP 3 — STATUS
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat(`Creating experience "${pending.label}"...`);
        speak(`Creating experience ${pending.label}`);

        window.dispatchEvent(
            new CustomEvent("ai-create-experience", {
                detail: {
                    label: pending.label,
                    timePeriod: pending.timePeriod,
                    status
                }
            })
        );

        setPending(null);
    }
};

export const startDeleteExperienceFlow = (
    setPending: (data: PendingDeleteExperience | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    labelFromIntent?: string
) => {
    if (labelFromIntent) {
        pushAIChat(`Deleting experience "${labelFromIntent}"...`);
        speak(`Deleting experience ${labelFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-experience", {
                detail: { label: labelFromIntent }
            })
        );
        return;
    }

    setPending({ awaitingLabel: true });
    pushAIChat("Which experience would you like to delete?");
    speak("Which experience would you like to delete?");
};

export const processPendingDeleteExperience = (
    text: string,
    pending: PendingDeleteExperience,
    setPending: (data: PendingDeleteExperience | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingLabel) return;

    const cleanLabel = text.trim();
    if (!cleanLabel) {
        pushAIChat("Please provide a valid experience name.");
        speak("Please provide a valid experience name.");
        return;
    }

    pushAIChat(`Deleting experience "${cleanLabel}"...`);
    speak(`Deleting experience ${cleanLabel}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-experience", {
            detail: { label: cleanLabel }
        })
    );

    setPending(null);
};
